import { Filesystem, Directory, type FileInfo } from '@capacitor/filesystem'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import SparkMD5 from 'spark-md5'
import api from '../../../lib/api'
import { debugLog } from '../../../lib/debugLog'
import { queryMediaStoreAudio, readAndroidCallLog, requestCallLogPermissions } from './androidCallLog'

// Always scan all three brands regardless of admin OEM setting
const ALL_OEM_PATHS = [
  'Call recordings',                  // Samsung
  'MIUI/sound_recorder/call_rec',     // Xiaomi
  'Recordings/Call',                  // Realme
]

const AUDIO_EXTENSIONS = ['.m4a', '.mp3', '.wav', '.aac', '.3gp']
const POLL_INTERVAL_MS = 5_000
const POLL_MAX_ATTEMPTS = 12   // 60s total

export interface RecordingFile {
  path: string        // full relative path (folder/filename) OR absolute path from MediaStore
  name: string
  mtime: number       // epoch ms
  size: number
  absolutePath?: boolean  // true = path is absolute (MediaStore); false/undefined = relative (ExternalStorage)
}

export async function requestRecordingPermission(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false
  try {
    // Request filesystem storage permission (READ_EXTERNAL_STORAGE on ≤API32)
    const fsResult = await Filesystem.requestPermissions()
    const clResult = await requestCallLogPermissions()
    const fsGranted = fsResult.publicStorage === 'granted'
    const audioGranted = clResult?.mediaAudio === 'granted' || clResult?.storage === 'granted'
    const callLogGranted = clResult?.callLog === 'granted'
    if (!fsGranted && !audioGranted) debugLog('Recording', 'requestPermission: audio permission denied', { fsGranted, audioGranted, callLogGranted })
    if (!callLogGranted) debugLog('Recording', 'requestPermission: callLog permission denied', { callLogGranted })
    return fsGranted || audioGranted
  } catch (e) {
    debugLog('Recording', 'requestPermission error', String(e))
    return false
  }
}

/**
 * List all audio files in all OEM folders newer than `since`.
 * Deduplicates by (name + size) across brands.
 */
export async function listNewRecordings(since: Date): Promise<RecordingFile[]> {
  const cutoff = since.getTime()
  const seen = new Set<string>()
  const results: RecordingFile[] = []

  for (const folder of ALL_OEM_PATHS) {
    try {
      const { files } = await Filesystem.readdir({
        path: folder,
        directory: Directory.ExternalStorage,
      })

      const audioFiles = (files as FileInfo[]).filter(f =>
        AUDIO_EXTENSIONS.some(ext => f.name.toLowerCase().endsWith(ext))
      )
      for (const f of audioFiles) {
        const mtime = f.mtime ?? 0
        if (mtime < cutoff) continue

        const dedupeKey = `${f.name}:${f.size}`
        if (seen.has(dedupeKey)) continue
        seen.add(dedupeKey)

        results.push({ path: `${folder}/${f.name}`, name: f.name, mtime, size: f.size ?? 0 })
      }
    } catch { /* folder absent — skip */ }
  }
  return results
}

/**
 * Find a single recording file.
 *
 * anchor: the reference timestamp for matching — use callEndedAt when known
 * (call log start + duration), otherwise fall back to callStartedAt.
 * Samsung writes the file when the call ends, so mtime ≈ callEndedAt.
 * We look for files within ±2 min of the anchor (tight window = fewer false matches).
 * If anchor is callStartedAt we use a 5-min forward window to cover any call length.
 *
 * Strategy:
 *   1. Scan all OEM folders in parallel (filesystem readdir)
 *   2. If nothing found, query Android MediaStore (Samsung S23 One UI 5/6+ fallback)
 */
async function findRecordingFile(
  callStartedAt: Date,
  callEndedAt?: Date,
): Promise<{ path: string; absolutePath: boolean } | null> {
  const anchor = (callEndedAt ?? callStartedAt).getTime()
  const callTime = callStartedAt.getTime()
  // Tight ±2 min window around call-end if we have it; broad 5 min forward window otherwise
  const MATCH_WINDOW_MS = callEndedAt ? 2 * 60_000 : 5 * 60_000
  const matchStart = callEndedAt ? anchor - MATCH_WINDOW_MS : callTime
  const matchEnd   = anchor + MATCH_WINDOW_MS

  // ── Phase 1: filesystem folder scan ──────────────────────────────────────
  const folderResults = await Promise.all(
    ALL_OEM_PATHS.map(async folder => {
      try {
        const { files } = await Filesystem.readdir({
          path: folder,
          directory: Directory.ExternalStorage,
        })
        const audioFiles = (files as FileInfo[]).filter(f =>
          AUDIO_EXTENSIONS.some(ext => f.name.toLowerCase().endsWith(ext))
        )
        const match = audioFiles
          .filter(f => {
            const mtime = f.mtime ?? 0
            return mtime >= matchStart && mtime <= matchEnd
          })
          .sort((a, b) => Math.abs((a.mtime ?? 0) - anchor) - Math.abs((b.mtime ?? 0) - anchor))[0]
        if (match) {
          debugLog('Recording', 'findRecordingFile: matched', { folder, name: match.name })
          return `${folder}/${match.name}`
        }
      } catch { /* folder absent — skip */ }
      return null
    })
  )

  const folderMatch = folderResults.find(r => r !== null) ?? null
  if (folderMatch) return { path: folderMatch, absolutePath: false }

  // ── Phase 2: MediaStore fallback (Samsung One UI 5/6+, any OEM that hides recordings) ──
  const mediaFiles = await queryMediaStoreAudio(callTime)
  const AUDIO_EXTS_SET = new Set(AUDIO_EXTENSIONS)
  const mediaMatch = mediaFiles
    .filter(f => {
      const ext = '.' + f.displayName.split('.').pop()?.toLowerCase()
      if (!AUDIO_EXTS_SET.has(ext)) return false
      return f.dateAdded >= matchStart - 60_000 && f.dateAdded <= matchEnd + 60_000
    })
    .sort((a, b) => Math.abs(a.dateAdded - anchor) - Math.abs(b.dateAdded - anchor))[0]

  if (mediaMatch) {
    debugLog('Recording', 'findRecordingFile: matched via MediaStore', { name: mediaMatch.displayName })
    return { path: mediaMatch.path, absolutePath: true }
  }

  debugLog('Recording', 'findRecordingFile: no match found', {
    callEndedAt: new Date(anchor).toISOString(),
    mediaStoreFiles: mediaFiles.length,
    newest: mediaFiles.slice(0, 3).map(f => ({
      name: f.displayName,
      secFromCallEnd: Math.round((f.dateAdded - anchor) / 1000),
      sizeKB: Math.round(f.size / 1024),
    })),
  })
  return null
}

/**
 * Wait for the app to go inactive (user in dialer) then come back active (call ended).
 * Times out after timeoutMs if the app never goes inactive (e.g. Exotel flow).
 */
async function waitForCallEnd(timeoutMs = 120_000): Promise<void> {
  return new Promise(resolve => {
    let wentInactive = false
    const timer = setTimeout(() => {
      handle.then(h => h.remove())
      debugLog('Recording', 'waitForCallEnd: timed out — no inactive→active cycle detected')
      resolve()
    }, timeoutMs)

    const handle = App.addListener('appStateChange', state => {
      if (!state.isActive) { wentInactive = true; return }
      if (wentInactive) {
        clearTimeout(timer)
        handle.then(h => h.remove())
        resolve()
      }
    })
  })
}

/**
 * Read a recording file and return its bytes + MD5 hex.
 * absolutePath=true → path is a full OS path (MediaStore); omit directory param.
 * absolutePath=false/undefined → path is relative to ExternalStorage.
 */
export async function readRecordingBytes(
  filePath: string,
  absolutePath?: boolean,
): Promise<{ bytes: Uint8Array; md5: string; ext: string } | null> {
  try {
    const result = await Filesystem.readFile(
      absolutePath
        ? { path: filePath }
        : { path: filePath, directory: Directory.ExternalStorage }
    )

    const binaryStr = atob(result.data as string)
    const bytes = new Uint8Array(binaryStr.length)
    for (let i = 0; i < binaryStr.length; i++) {
      bytes[i] = binaryStr.charCodeAt(i)
    }

    const spark = new SparkMD5.ArrayBuffer()
    spark.append(bytes.buffer)
    const md5 = spark.end()

    const ext = filePath.split('.').pop()?.toLowerCase() ?? 'm4a'
    return { bytes, md5, ext }
  } catch {
    return null
  }
}

async function uploadFile(filePath: string, callLogId: number, absolutePath?: boolean): Promise<boolean> {
  const data = await readRecordingBytes(filePath, absolutePath)
  if (!data) {
    debugLog('Recording', 'upload error: could not read file', { filePath })
    return false
  }

  const { bytes, md5, ext } = data
  const blob = new Blob([bytes.buffer as ArrayBuffer], { type: `audio/${ext}` })

  // Wait 3s — mobile data briefly drops after returning from the dialer
  await new Promise(r => setTimeout(r, 3_000))

  const form = new FormData()
  form.append('file', blob, `call_${callLogId}.${ext}`)
  form.append('md5', md5)

  try {
    await api.post(`/telephony/calls/${callLogId}/recording`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60_000,
    })
    debugLog('Recording', 'uploaded', { callLogId, sizeBytes: bytes.length })
    return true
  } catch (e: any) {
    debugLog('Recording', 'upload failed', {
      callLogId,
      status: e?.response?.status,
      error: e?.message ?? String(e),
    })
    return false
  }
}

export type PollResult = 'uploaded' | 'not_found' | 'ios'

/**
 * Wait for the call to end then poll all OEM folders for the recording.
 *
 * Call this immediately after window.open('tel:...') — it attaches its own
 * appStateChange listener to detect inactive→active (call ended) before
 * starting to poll, avoiding premature scans mid-call.
 *
 * The `oem` parameter is kept for API compatibility but no longer affects
 * which folders are scanned — all vendor folders are checked in parallel.
 */
export async function pollAndUpload(
  callLogId: number,
  callStartedAt: Date,
  _oem?: string,
): Promise<PollResult> {
  if (!Capacitor.isNativePlatform()) return 'ios'

  await waitForCallEnd()

  let callEndedAt: Date | undefined
  try {
    const callLogEntries = await readAndroidCallLog(callStartedAt)
    if (callLogEntries.length > 0) {
      const best = callLogEntries.sort(
        (a, b) => Math.abs(a.started_at.getTime() - callStartedAt.getTime())
                - Math.abs(b.started_at.getTime() - callStartedAt.getTime())
      )[0]
      callEndedAt = new Date(best.started_at.getTime() + best.duration_s * 1000)
    } else {
      debugLog('Recording', 'no call log entry — time-window match may be less accurate', { callLogId })
    }
  } catch (e) {
    debugLog('Recording', 'call log query error', { callLogId, error: String(e) })
  }

  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
    const found = await findRecordingFile(callStartedAt, callEndedAt)
    if (found) {
      const ok = await uploadFile(found.path, callLogId, found.absolutePath)
      return ok ? 'uploaded' : 'not_found'
    }
    if (attempt < POLL_MAX_ATTEMPTS - 1) {
      await new Promise(r => setTimeout(r, POLL_INTERVAL_MS))
    }
  }
  debugLog('Recording', 'recording not found after all attempts', { callLogId, callStartedAt: callStartedAt.toISOString() })
  return 'not_found'
}
